import React, { useState, useEffect, useCallback } from "react";
import { CSpinner } from "@coreui/react-pro";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/eye-slash.svg";
import {
  getRoleNames,
  getOrder,
  createPolicy,
  uploadPolicyFiles,
  getPolicy,
  updatePolicy,
} from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";

const CreatePolicy = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [order, setOrder] = useState({});
  const [policy, setPolicy] = useState({});
  const [policyDocs, setPolicyDocs] = useState([]);
  const { id, policyId } = useParams();
  const [originalValues, setOriginalValues] = useState({});
  const [isInsuranceTypeFive, setIsInsuranceTypeFive] = useState(false);

  const [initialValues, setInitialValues] = useState({
    status: "",
    policy_start_date: "",
    policy_end_date: "",
    subscription_plan_name: "",
    supporting_docs: [],
  });

  const fetchPolicy = async (id) => {
    setIsLoading(true);
    try {
      const response = await getPolicy({ id: id });
      console.log(response.data);
      if (response.data) {
        setOrder(response.data);
        const fetchedValues = {
          status: response.data.status || "",
          supporting_docs: response.data.supporting_docs || [],
          subscription_plan_name: response.data.subscription_plan_name || "",
          cover_note_no: response.data.cover_note_no || "",
          policy_start_date: response.data.policy_start_date
            ? new Date(
                new Date(response.data.policy_start_date).getTime() -
                  new Date().getTimezoneOffset() * 60000
              )
                .toISOString()
                .split("T")[0]
            : "",
          policy_end_date: response.data.policy_end_date
            ? new Date(
                new Date(response.data.policy_end_date).getTime() -
                  new Date().getTimezoneOffset() * 60000
              )
                .toISOString()
                .split("T")[0]
            : "",
        };
        setInitialValues(fetchedValues);
        setOriginalValues(fetchedValues);
        setPolicyDocs(response.data.policy_docs || []);
        setIsInsuranceTypeFive(
          response.data.order_details.insurance_type === 5
        );
      } else {
        //setOrder({});
        console.error("No data found for the given ID");
        toast.error("No data found for the given ID");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const response = await getOrder({ id: id });
      if (response.data) {
        console.log(response.data);
        setOrder(response.data);
      } else {
        console.error("No data found for the given ID");
        toast.error("No data found for the given ID");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (policyId !== null && policyId !== "undefined") {
      fetchPolicy(policyId);
    } else {
      fetchData(id);
      console.error("No ID provided in URL parameters");
    }
  }, [policyId]);

  const validationSchema = Yup.object().shape({
    status: Yup.string().required("Status is required"),
    policy_start_date: Yup.date().required("Policy start date is required"),
    policy_end_date: Yup.date()
      .required("Policy end date is required")
      .min(
        Yup.ref("policy_start_date"),
        "Policy end date cannot be before start date"
      ),
    subscription_plan_name: Yup.string()
      .required("Subscription plan name is required")
      .min(3, "Subscription plan name must be at least 3 characters")
      .max(50, "Subscription plan name must be at most 50 characters"),
    supporting_docs: Yup.array()
      .of(Yup.mixed().required("A file is required"))
      .min(1, "Policy documents are required"),
  });

  const disablePastDates = () => {
    const today = new Date().toISOString().split("T")[0];
    return today;
  };

  const disableFutureDates = (endDate) => {
    return endDate ? new Date(endDate).toISOString().split("T")[0] : null;
  };

  const handleStartDateChange = (event, setFieldValue, values) => {
    const startDate = event.target.value;
    if (
      values.policy_end_date &&
      new Date(startDate) > new Date(values.policy_end_date)
    ) {
      toast.error("Policy start date cannot be after end date");
    } else {
      setFieldValue("policy_start_date", startDate);
    }
  };

  const handleEndDateChange = (event, setFieldValue, values) => {
    const endDate = event.target.value;
    if (new Date(endDate) < new Date(values.policy_start_date)) {
      toast.error("Policy end date cannot be before start date");
    } else {
      setFieldValue("policy_end_date", endDate);
    }
  };

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      console.log(order);
      if (!order || Object.keys(order).length === 0) {
        throw new Error("Order data is not available");
      }
      if (
        !Array.isArray(values.supporting_docs) ||
        values.supporting_docs.length === 0
      ) {
        setErrors({ supporting_docs: "Policy documents are required" });
        setSubmitting(false);
        setIsSubmittingForm(false);
        return;
      }

      // Create FormData and append files
      const formData = new FormData();
      values.supporting_docs.forEach((file) => {
        formData.append("files", file);
      });

      // Upload files
      const uploadResponse = await uploadPolicyFiles(formData);
      if (
        !uploadResponse.data.files ||
        uploadResponse.data.files.length === 0
      ) {
        throw new Error("Failed to upload policy documents");
      }
      toast.success("Policy documents uploaded successfully!");

      // Update supporting_docs with URLs
      const supportingDocsUrls = uploadResponse.data.files.map(
        (file) => `${process.env.REACT_APP_API_URL}/uploads/${file}`
      );

      const payload = {
        ...values,
        supporting_docs: supportingDocsUrls,
        user_id: order.user_id,
        order_id: order.id,
      };

      await createPolicy(payload);
      toast.success("Policy created successfully!");
      navigate("/insurance-policies");
    } catch (error) {
      console.error("Failed to create policy:", error);
      toast.error(
        error.message || "Failed to create policy. Please try again."
      );
      setErrors({ submit: "Failed to create policy", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  const handleUploadPolicy = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      let supportingDocsUrls = [];

      if (
        Array.isArray(values.supporting_docs) &&
        values.supporting_docs.length > 0
      ) {
        const formData = new FormData();
        values.supporting_docs.forEach((file) => {
          formData.append("files", file);
        });

        // Upload files
        const uploadResponse = await uploadPolicyFiles(formData);
        if (
          !uploadResponse.data.files ||
          uploadResponse.data.files.length === 0
        ) {
          throw new Error("Failed to upload policy documents");
        }

        // Update supporting_docs with URLs
        supportingDocsUrls = uploadResponse.data.files.map(
          (file) => `${process.env.REACT_APP_API_URL}/uploads/${file}`
        );
      }

      const payload = {
        ...values,
        id: id,
        ...(supportingDocsUrls.length > 0
          ? { supporting_docs: supportingDocsUrls }
          : {}),
      };

      if (supportingDocsUrls.length === 0) {
        delete payload.supporting_docs;
      }

      if (isInsuranceTypeFive) {
        delete payload.cover_note_no;
        delete payload.policy_start_date;
        delete payload.policy_end_date;
      }

      const hasChanges = Object.keys(values).some((key) => {
        if (key === "supporting_docs") {
          return values[key].length !== originalValues[key].length;
        }
        return values[key] !== originalValues[key];
      });

      if (hasChanges) {
        const response = await updatePolicy(payload);
        if (response && response.data) {
          toast.success("Policy updated successfully!");
          navigate("/orders/");
        } else {
          throw new Error("Failed to update policy");
        }
      } else {
        toast.info("No changes detected.");
        navigate("/orders/");
      }
    } catch (error) {
      console.error("Failed to edit policy:", error);
      toast.error(error.message || "Failed to edit policy. Please try again.");
      setErrors({ submit: "Failed to edit policy", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Pending", label: "Pending" },
    { value: "Expired", label: "Expired" },
    { value: "Pending Policy", label: "Pending Policy" },
    { value: "Upcoming Renewals", label: "Upcoming Renewals" },
  ];

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={policyId.id ? handleUploadPolicy : handleSubmit}
        >
          {({ isSubmitting, errors, touched, setFieldValue, values }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  {/* Form fields */}
                  <div className="row">
                    {/* Subscription Plan Name */}
                    <div className="col-md-6 mb-3">
                      <label
                        htmlFor="subscription_plan_name"
                        className="form-label"
                      >
                        <span className="text-danger">*</span> Subscription Plan
                        Name
                      </label>
                      <Field
                        name="subscription_plan_name"
                        as="input"
                        type="text"
                        className={`form-control ${
                          touched.subscription_plan_name &&
                          errors.subscription_plan_name
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="subscription_plan_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Status */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="status" className="form-label">
                        <span className="text-danger">*</span> Status
                      </label>
                      <Field
                        name="status"
                        as="select"
                        className={`form-control ${
                          touched.status && errors.status ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select status</option>
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="status"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    {/* Policy Start Date */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="policy_start_date" className="form-label">
                        <span className="text-danger">*</span> Policy Start Date
                      </label>
                      <Field
                        name="policy_start_date"
                        as="input"
                        type="date"
                        min={disablePastDates()}
                        max={disableFutureDates(values.policy_end_date)}
                        className={`form-control ${
                          touched.policy_start_date && errors.policy_start_date
                            ? "is-invalid"
                            : ""
                        }`}
                        onChange={(event) =>
                          handleStartDateChange(event, setFieldValue, values)
                        }
                      />
                      <ErrorMessage
                        name="policy_start_date"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Policy End Date */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="policy_end_date" className="form-label">
                        <span className="text-danger">*</span> Policy End Date
                      </label>
                      <Field
                        name="policy_end_date"
                        as="input"
                        type="date"
                        min={values.policy_start_date || disablePastDates()}
                        className={`form-control ${
                          touched.policy_end_date && errors.policy_end_date
                            ? "is-invalid"
                            : ""
                        }`}
                        onChange={(event) =>
                          handleEndDateChange(event, setFieldValue, values)
                        }
                      />
                      <ErrorMessage
                        name="policy_end_date"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    {/* Policy Documents */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="supporting_docs" className="form-label">
                        <span className="text-danger">*</span> Policy Documents
                      </label>
                      <input
                        id="supporting_docs"
                        name="supporting_docs"
                        type="file"
                        className={`form-control ${
                          touched.supporting_docs && errors.supporting_docs
                            ? "is-invalid"
                            : ""
                        }`}
                        onChange={(event) => {
                          setFieldValue(
                            "supporting_docs",
                            Array.from(event.currentTarget.files)
                          );
                        }}
                        multiple
                      />
                      <ErrorMessage
                        name="supporting_docs"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-8 mb-3">
                      <label className="form-label">
                        Existing Policy Documents:
                      </label>
                      {policyDocs && policyDocs.length > 0 ? (
                        policyDocs.map((doc) => (
                          <div key={doc.id}>
                            <a
                              href={doc.document}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {doc.document.split("/").pop()}
                            </a>
                          </div>
                        ))
                      ) : (
                        <p>
                          <b>N/A</b>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={() => navigate("/orders")}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      {policyId !== "undefined"
                        ? "Upload Policy"
                        : "Create Policy"}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default CreatePolicy;
